// Email Template Generator v1.0 — Upgraded Technical Response Email System
// Generates structured, professional client-facing email content
// Based on EnergiPro Solutions communication strategy

import { RFQState, SERVICE_TYPES, COUNTRY_OPTIONS } from './rfqConfig';
import { RuleEngineOutput } from './ruleEngine';
import { generateEmailContactBlock } from './multiChannelContactEngine';

// ─── TYPES ───────────────────────────────────────────────────────────────────

export interface EmailTemplate {
  subject: string;
  sections: EmailSection[];
  metadata: EmailMetadata;
}

export interface EmailSection {
  id: string;
  label: string;
  content: string;
  type: 'opening' | 'understanding' | 'solution' | 'attachments' | 'assumptions' | 'cta' | 'close';
}

export interface EmailMetadata {
  referenceId: string;
  generatedAt: string;
  clientName: string;
  companyName: string;
  service: string;
  portalLink: string;
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function getServiceLabel(serviceId: string): string {
  return SERVICE_TYPES.find(s => s.id === serviceId)?.label || serviceId;
}

function getCountryLabel(countryId: string): string {
  return COUNTRY_OPTIONS.find(c => c.id === countryId)?.label || countryId;
}

function getPressureRating(psi: number): string {
  if (psi >= 10000) return '10,000 PSI';
  if (psi >= 5000) return '5,000 PSI';
  if (psi >= 3000) return '3,000 PSI';
  if (psi >= 1440) return '1,440 PSI';
  return 'Standard';
}

function getDurationLabel(duration: string): string {
  if (duration === 'short-term') return '14 days (short-term campaign)';
  if (duration === 'long-term') return '60 days (extended operations)';
  return 'To be confirmed';
}

function getTimelineLabel(timeline: string): string {
  if (timeline === 'immediate') return 'Immediate mobilization required';
  if (timeline === '1-2-weeks') return '1–2 weeks lead time';
  if (timeline === 'planned') return 'Planned deployment (standard scheduling)';
  return 'To be confirmed';
}

function buildConditionSummary(state: RFQState): string {
  const conditions: string[] = [];
  const pressure = parseFloat(state.wellheadPressure) || 0;
  if (pressure > 0) conditions.push(`${pressure.toLocaleString()} PSI wellhead pressure`);
  if (state.h2sPresence === 'yes') conditions.push('H₂S present');
  if (state.sandProduction && state.sandProduction !== 'low') conditions.push(`${state.sandProduction} sand production`);
  const oilRate = parseFloat(state.oilRate) || 0;
  if (oilRate > 0) conditions.push(`${oilRate.toLocaleString()} BOPD oil rate`);
  const gasRate = parseFloat(state.gasRate) || 0;
  if (gasRate > 0) conditions.push(`${gasRate} MMSCFD gas rate`);
  if (state.environment) conditions.push(`${state.environment} environment`);
  return conditions.join(' / ');
}

// ─── GENERATOR ───────────────────────────────────────────────────────────────

export function generateEmailTemplate(
  state: RFQState,
  ruleOutput: RuleEngineOutput,
  referenceId: string
): EmailTemplate {
  const clientName = state.fullName || 'Valued Client';
  const companyName = state.companyName || 'Client Organization';
  const serviceLabel = getServiceLabel(state.service);
  const countryLabel = getCountryLabel(state.country);
  const pressure = parseFloat(state.wellheadPressure) || 0;
  const pressureRating = getPressureRating(pressure);
  const durationLabel = getDurationLabel(state.duration);
  const timelineLabel = getTimelineLabel(state.timeline);
  const conditionSummary = buildConditionSummary(state);
  const portalLink = `/portal?ref=${referenceId}`;

  // Subject Line
  const subject = `Proposed Well Testing Solution — Ref: ${referenceId}`;

  // Section 1: Opening
  const opening: EmailSection = {
    id: 'opening',
    label: 'Opening',
    type: 'opening',
    content: `Dear ${clientName},

Thank you for reaching out — we've reviewed your requirements and put together a technical solution that fits your operation.

Our engineering team has gone through the data you provided and selected a configuration that matches your operating conditions and production expectations. Here's what we've come up with:`,
  };

  // Section 2: Understanding of Requirement
  const understanding: EmailSection = {
    id: 'understanding',
    label: 'Understanding of Requirement',
    type: 'understanding',
    content: `Based on your submission, the operation involves:

• Service: ${serviceLabel}
• Location: ${countryLabel}${state.locationType ? ` (${state.locationType})` : ''}${state.environment ? ` — ${state.environment === 'onshore' ? 'Onshore' : 'Offshore'}` : ''}
• Estimated Duration: ${durationLabel}
• Timeline: ${timelineLabel}
• Key Conditions: ${conditionSummary || 'Standard operating conditions'}

This scenario requires a robust and field-proven configuration to ensure safe and efficient execution.`,
  };

  // Section 3: Proposed Technical Approach
  const primaryEquip = ruleOutput.equipmentList.filter(e => e.category === 'primary');
  const safetyEquip = ruleOutput.equipmentList.filter(e => e.category === 'safety');
  const supportEquip = ruleOutput.equipmentList.filter(e => e.category === 'support');

  const solutionItems: string[] = [];
  if (primaryEquip.length > 0) {
    solutionItems.push(`• Surface well testing spread (${primaryEquip.slice(0, 3).map(e => e.name.toLowerCase()).join(', ')})`);
  }
  if (state.sandProduction && state.sandProduction !== 'low') {
    solutionItems.push('• Sand management system (desander + filtration)');
  }
  solutionItems.push('• Flare system with standard burner configuration');
  solutionItems.push('• Instrumentation (pressure, temperature, flow measurement)');
  if (safetyEquip.length > 0 || state.h2sPresence === 'yes') {
    solutionItems.push('• Full safety system (H₂S detection, emergency response setup)');
  } else {
    solutionItems.push('• Safety system (ESD, fire & gas detection)');
  }
  if (supportEquip.length > 0) {
    solutionItems.push(`• Support equipment (${supportEquip.slice(0, 2).map(e => e.name.toLowerCase()).join(', ')})`);
  }

  const solution: EmailSection = {
    id: 'solution',
    label: 'Proposed Technical Approach',
    type: 'solution',
    content: `Based on your conditions, we'd recommend a ${pressureRating} well testing package that includes:

${solutionItems.join('\n')}

We've selected this configuration to match your operating envelope while keeping things safe and efficient. Happy to walk through any of this with you.`,
  };

  // Section 4: Attached Documents
  const attachments: EmailSection = {
    id: 'attachments',
    label: 'Attached Documents',
    type: 'attachments',
    content: `The following documents are included with this response:

• Structured RFQ Summary
• Preliminary Technical Response
• Equipment Configuration Overview

These documents provide full technical details and assumptions used in the design.`,
  };

  // Section 5: Key Assumptions
  const assumptions: EmailSection = {
    id: 'assumptions',
    label: 'Key Assumptions',
    type: 'assumptions',
    content: `Just to be transparent — this configuration is based on what you've shared so far. We can fine-tune the final equipment selection once we receive:

• Detailed well data (completion details, reservoir characterization)
• Confirmed production parameters (expected rates, GOR, water cut)
• Site logistics constraints (access road conditions, crane availability, laydown area)
${state.environment === 'offshore' ? '• Vessel/platform specifications and lifting capacity\n' : ''}${state.h2sPresence === 'yes' ? '• H₂S concentration levels and safety zone requirements\n' : ''}
All assumptions are documented in the attached technical response.`,
  };

  // Section 6: Next Step (CTA)
  const cta: EmailSection = {
    id: 'cta',
    label: 'Next Step',
    type: 'cta',
    content: `We'd suggest a quick technical call to confirm assumptions and lock in the final configuration. Shouldn't take more than 15–20 minutes.

Here's how to move forward:

• Reply to this email — we'll get back same day
• Request a technical call with our ops engineering team
• Share any additional data for refinement
• Access your full proposal via our client portal
${generateEmailContactBlock('high', referenceId, serviceLabel)}
Our team is ready to proceed immediately based on your schedule.`,
  };

  // Section 7: Authority Close
  const close: EmailSection = {
    id: 'close',
    label: 'Closing',
    type: 'close',
    content: `We've been delivering integrated oilfield services across KSA and the GCC for years — well testing, pressure control, engineering, and equipment supply, all under our IMS-controlled execution standards.

Looking forward to supporting your operation. Don't hesitate to reach out if you have any questions.

Best regards,
EnergiPro Solutions — Operations Engineering
Al Khobar, Eastern Province, KSA`,
  };

  return {
    subject,
    sections: [opening, understanding, solution, attachments, assumptions, cta, close],
    metadata: {
      referenceId,
      generatedAt: new Date().toISOString(),
      clientName,
      companyName,
      service: serviceLabel,
      portalLink,
    },
  };
}

// ─── PLAIN TEXT RENDERER ─────────────────────────────────────────────────────

export function renderEmailAsPlainText(template: EmailTemplate): string {
  const lines: string[] = [];

  lines.push(`Subject: ${template.subject}`);
  lines.push('');
  lines.push('─'.repeat(60));
  lines.push('');

  for (const section of template.sections) {
    if (section.type !== 'opening' && section.type !== 'close') {
      lines.push(`━━━ ${section.label.toUpperCase()} ━━━`);
      lines.push('');
    }
    lines.push(section.content);
    lines.push('');
  }

  lines.push('─'.repeat(60));
  lines.push(`Reference: ${template.metadata.referenceId}`);
  lines.push(`Generated: ${new Date(template.metadata.generatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`);

  return lines.join('\n');
}

// ─── HTML RENDERER ───────────────────────────────────────────────────────────

export function renderEmailAsHTML(template: EmailTemplate): string {
  const portalUrl = template.metadata.portalLink;

  const sectionToHTML = (section: EmailSection): string => {
    const content = section.content
      .replace(/\n/g, '<br>')
      .replace(/• /g, '<span style="color:#ea580c;font-weight:bold;">●</span> ');

    switch (section.type) {
      case 'opening':
        return `<div style="margin-bottom:24px;line-height:1.7;color:#1e293b;">${content}</div>`;

      case 'understanding':
        return `
          <div style="margin-bottom:24px;padding:20px;background:#f8fafc;border-left:4px solid #ea580c;border-radius:4px;">
            <h3 style="margin:0 0 12px;font-family:'Oswald',sans-serif;font-size:14px;text-transform:uppercase;letter-spacing:1px;color:#ea580c;">Understanding of Requirement</h3>
            <div style="line-height:1.8;color:#334155;font-size:14px;">${content}</div>
          </div>`;

      case 'solution':
        return `
          <div style="margin-bottom:24px;padding:20px;background:#0f172a;border-radius:8px;">
            <h3 style="margin:0 0 12px;font-family:'Oswald',sans-serif;font-size:14px;text-transform:uppercase;letter-spacing:1px;color:#fb923c;">Proposed Technical Approach</h3>
            <div style="line-height:1.8;color:#e2e8f0;font-size:14px;">${content}</div>
          </div>`;

      case 'attachments':
        return `
          <div style="margin-bottom:24px;padding:16px;border:1px solid #e2e8f0;border-radius:6px;">
            <h3 style="margin:0 0 12px;font-family:'Oswald',sans-serif;font-size:14px;text-transform:uppercase;letter-spacing:1px;color:#475569;">Attached Documents</h3>
            <div style="line-height:1.8;color:#334155;font-size:14px;">${content}</div>
          </div>`;

      case 'assumptions':
        return `
          <div style="margin-bottom:24px;padding:16px;background:#fffbeb;border:1px solid #fde68a;border-radius:6px;">
            <h3 style="margin:0 0 12px;font-family:'Oswald',sans-serif;font-size:14px;text-transform:uppercase;letter-spacing:1px;color:#92400e;">Key Assumptions</h3>
            <div style="line-height:1.8;color:#78350f;font-size:14px;">${content}</div>
          </div>`;

      case 'cta':
        return `
          <div style="margin-bottom:24px;padding:24px;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;text-align:center;">
            <h3 style="margin:0 0 12px;font-family:'Oswald',sans-serif;font-size:14px;text-transform:uppercase;letter-spacing:1px;color:#166534;">Next Step</h3>
            <div style="line-height:1.8;color:#166534;font-size:14px;text-align:left;margin-bottom:20px;">${content}</div>
            <a href="${portalUrl}" style="display:inline-block;padding:14px 32px;background:#ea580c;color:#ffffff;font-family:'Oswald',sans-serif;font-size:14px;text-transform:uppercase;letter-spacing:1px;text-decoration:none;border-radius:4px;font-weight:600;">Review Full Proposal</a>
          </div>`;

      case 'close':
        return `
          <div style="margin-top:32px;padding-top:20px;border-top:2px solid #e2e8f0;line-height:1.7;color:#64748b;font-size:13px;">${content}</div>`;

      default:
        return `<div style="margin-bottom:24px;line-height:1.7;color:#1e293b;">${content}</div>`;
    }
  };

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin:0;padding:0;background:#f1f5f9;font-family:'Roboto',Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f1f5f9;padding:32px 16px;">
    <tr>
      <td align="center">
        <table width="640" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 4px 6px rgba(0,0,0,0.05);">
          <!-- Header -->
          <tr>
            <td style="background:#0f172a;padding:24px 32px;">
              <table width="100%">
                <tr>
                  <td>
                    <span style="font-family:'Oswald',sans-serif;font-size:20px;color:#ffffff;letter-spacing:1px;font-weight:600;">ENERGIPRO SOLUTIONS</span>
                  </td>
                  <td align="right">
                    <span style="font-family:monospace;font-size:12px;color:#94a3b8;">${template.metadata.referenceId}</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- Body -->
          <tr>
            <td style="padding:32px;">
              ${template.sections.map(sectionToHTML).join('\n')}
            </td>
          </tr>
          <!-- Footer -->
          <tr>
            <td style="background:#1e293b;padding:20px 32px;">
              <table width="100%">
                <tr>
                  <td>
                    <span style="font-size:11px;color:#94a3b8;">EnergiPro Solutions | Al Khobar, KSA</span>
                  </td>
                  <td align="right">
                    <span style="font-size:11px;color:#64748b;">Ref: ${template.metadata.referenceId}</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}
